import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";

import { createBlog, updateBlog, clearBlogErrors } from "../../features/blog/blogSlice";
import "react-toastify/dist/ReactToastify.css";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
];

const sanitizeDescription = (description) => DOMPurify.sanitize(description);

const isDescriptionEmpty = (description) => {
  const cleaned = DOMPurify.sanitize(description, { ALLOWED_TAGS: [] })
    .replace(/&nbsp;/g, " ")
    .trim();
  return cleaned.length === 0;
};

const AddBlogForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const blog = location.state?.blog;
  const isEditMode = !!blog;

  const [title, setTitle] = useState(blog?.title || "");
  const [description, setDescription] = useState(blog?.description || "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(blog?.img || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (blog) {
      setTitle(blog.title || "");
      setDescription(blog.description || "");
      setImagePreview(blog.img || null);
    }
  }, [blog]);

  useEffect(() => {
    return () => {
      dispatch(clearBlogErrors());
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [dispatch, imagePreview]);

  const memoizedModules = useMemo(() => quillModules, []);
  const memoizedFormats = useMemo(() => quillFormats, []);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB.");
      return;
    }

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }

    if (!description || isDescriptionEmpty(description)) {
      toast.error("Description is required.");
      return;
    }

    if (!isEditMode && !imageFile) {
      toast.error("Please select an image.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        const blogId = blog?._id || blog?.id;
        if (!blogId) {
          toast.error("Blog ID not found.");
          setIsSubmitting(false);
          return;
        }

        await dispatch(
          updateBlog({
            blogId,
            title: title.trim(),
            description: sanitizeDescription(description),
            imageFile: imageFile || null,
          })
        ).unwrap();

        toast.success("Blog updated successfully.");
      } else {
        await dispatch(
          createBlog({
            title: title.trim(),
            description: sanitizeDescription(description),
            imageFile,
          })
        ).unwrap();

        toast.success("Blog created successfully.");
        setTitle("");
        setDescription("");
        setImageFile(null);
        if (imagePreview && imagePreview.startsWith("blob:")) {
          URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(null);
      }

      navigate("/blogs-list");
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : error?.message || (isEditMode ? "Failed to update blog." : "Failed to create blog.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card h-100 radius-12">
      <div className="card-header bg-base border-bottom py-16 px-24">
        <h5 className="mb-0">{isEditMode ? "Edit Blog" : "Add Blog"}</h5>
      </div>
      <div className="card-body p-24">
        <form className="row g-3" onSubmit={handleSubmit}>
          <div className="col-12">
            <label htmlFor="blogTitle" className="form-label">
              Title
            </label>
            <input
              id="blogTitle"
              type="text"
              className="form-control"
              placeholder="Enter blog title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Description</label>
            <ReactQuill
              theme="snow"
              value={description}
              onChange={setDescription}
              modules={memoizedModules}
              formats={memoizedFormats}
              readOnly={isSubmitting}
            />
          </div>

          <div className="col-12 col-md-6">
            <label htmlFor="blogImage" className="form-label">
              Image
            </label>
            <input
              id="blogImage"
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isSubmitting}
            />
            <small className="text-secondary-light d-block mt-8">
              Supported formats: JPG, PNG, GIF. Max size 5MB.
              {isEditMode && " Leave empty to keep current image."}
            </small>
          </div>

          {imagePreview && (
            <div className="col-12 col-md-6">
              <label className="form-label">Preview</label>
              <div className="border radius-12 overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Blog preview"
                  className="w-100 h-200 object-fit-cover"
                />
              </div>
            </div>
          )}

          <div className="col-12 d-flex justify-content-end gap-3 mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-bg"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Submitting..."
                : isEditMode
                ? "Update Blog"
                : "Create Blog"}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AddBlogForm;

